/**
 * Gelişim sekmesi — geri bildirimin davranışa dönüştüğü yer.
 *
 * Reflexion döngüsünün görünür yüzü: kullanıcının tartışmalarda verdiği
 * like/dislike/puan/not burada bir DERS listesine dönüşür ve o dersler
 * personanın bir sonraki turundaki system prompt'una girer. Model ağırlığı
 * güncellenmez — öğrenme tamamen metinseldir ve bu yüzden denetlenebilir:
 * kullanıcı her dersi görür, düzenler, kapatır veya siler.
 *
 * Ekranın en önemli işi bu şeffaflığı sağlamak. "Beğendim'e bastım, ne oldu?"
 * sorusunun cevabı burada birebir okunabilir olmalı.
 */
import { useCallback, useEffect, useState } from "react";
import type {
  ConsolidationResult,
  GrowthLesson,
  PersonaFeedback,
  PersonaSelfModel,
} from "../../domain/personaLibrary";
import {
  LibraryApiError,
  consolidateGrowth,
  deleteLesson,
  listLessons,
  listPersonaFeedback,
  patchLesson,
} from "../../transport/personaLibraryClient";
import { Btn, Chip, EmptyState, ErrorBanner, SectionTitle, TextArea } from "./ui";

function LessonRow({
  lesson,
  onChanged,
  onError,
}: {
  lesson: GrowthLesson;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(lesson.lesson);
  const [busy, setBusy] = useState(false);
  const reinforce = lesson.polarity === "reinforce";

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (err) {
      onError(err instanceof LibraryApiError ? err.message : "İşlem başarısız.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={[
        "rounded-xl border px-3.5 py-3 transition-opacity",
        lesson.isActive ? "" : "opacity-50",
        reinforce
          ? "border-emerald-500/20 bg-emerald-500/[0.04]"
          : "border-amber-500/20 bg-amber-500/[0.04]",
      ].join(" ")}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <Chip tone={reinforce ? "green" : "amber"}>{reinforce ? "SÜRDÜR" : "DÜZELT"}</Chip>
        <Chip title="Kaç ayrı geri bildirimin bu dersi doğruladığı">
          {lesson.evidenceCount} kanıt
        </Chip>
        <Chip title="Prompt'a girme önceliği">ağırlık {lesson.weight.toFixed(2)}</Chip>
        {!lesson.isActive && <Chip tone="rose">pasif</Chip>}
      </div>

      {editing ? (
        <TextArea
          rows={2}
          value={draft}
          maxLength={300}
          onChange={(e) => setDraft(e.target.value)}
        />
      ) : (
        <p className="text-[12.5px] leading-relaxed text-slate-200">{lesson.lesson}</p>
      )}

      {lesson.rationale && !editing && (
        <p className="mt-1 text-[11px] italic leading-relaxed text-slate-500">
          {lesson.rationale}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-white/[0.06] pt-2">
        {editing ? (
          <>
            <Btn
              size="sm"
              tone="primary"
              loading={busy}
              onClick={() =>
                void run(async () => {
                  await patchLesson(lesson.id, { lesson: draft.trim() });
                  setEditing(false);
                })
              }
            >
              Kaydet
            </Btn>
            <Btn
              size="sm"
              tone="ghost"
              onClick={() => {
                setDraft(lesson.lesson);
                setEditing(false);
              }}
            >
              Vazgeç
            </Btn>
          </>
        ) : (
          <>
            <Btn size="sm" tone="ghost" onClick={() => setEditing(true)} disabled={busy}>
              Düzenle
            </Btn>
            <Btn
              size="sm"
              tone="ghost"
              disabled={busy}
              onClick={() => void run(() => patchLesson(lesson.id, { isActive: !lesson.isActive }))}
            >
              {lesson.isActive ? "Devre dışı bırak" : "Etkinleştir"}
            </Btn>
            <Btn
              size="sm"
              tone="ghost"
              disabled={busy}
              className="hover:text-rose-400"
              onClick={() => void run(() => deleteLesson(lesson.id))}
            >
              Sil
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}

function FeedbackRow({ item }: { item: PersonaFeedback }) {
  const icon = item.verdict === "like" ? "👍" : item.verdict === "dislike" ? "👎" : "•";
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <span className="text-[14px] leading-none">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {item.rating !== null && <Chip tone="violet">{item.rating}/5</Chip>}
          {Object.entries(item.dimensions).map(([k, v]) => (
            <Chip key={k}>
              {k} {v}
            </Chip>
          ))}
          <Chip tone={item.processed ? "green" : "amber"}>
            {item.processed ? "işlendi" : "bekliyor"}
          </Chip>
        </div>
        {item.excerpt && (
          <p className="mt-1 border-l-2 border-white/10 pl-2 text-[11.5px] italic leading-relaxed text-slate-500">
            “{item.excerpt}”
          </p>
        )}
        {item.note && (
          <p className="mt-1 text-[12px] leading-relaxed text-slate-300">{item.note}</p>
        )}
      </div>
      <span className="shrink-0 text-[10.5px] text-slate-600">
        {new Date(item.createdAt).toLocaleDateString("tr-TR")}
      </span>
    </div>
  );
}

export function GrowthTab({
  personaId,
  lessons,
  selfModel,
  pendingFeedback,
  onLessonsChanged,
  onSelfModelStale,
}: {
  personaId: string;
  lessons: GrowthLesson[];
  selfModel: PersonaSelfModel;
  pendingFeedback: number;
  onLessonsChanged: (next: GrowthLesson[]) => void;
  onSelfModelStale: () => void;
}) {
  const [feedback, setFeedback] = useState<PersonaFeedback[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConsolidationResult | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setFeedback(await listPersonaFeedback(personaId));
      } catch (err) {
        setError(err instanceof LibraryApiError ? err.message : "Geri bildirim yüklenemedi.");
      }
    })();
  }, [personaId]);

  const refreshLessons = useCallback(async () => {
    try {
      onLessonsChanged(await listLessons(personaId));
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Dersler yüklenemedi.");
    }
  }, [personaId, onLessonsChanged]);

  async function handleConsolidate() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await consolidateGrowth(personaId);
      setResult(res);
      await refreshLessons();
      setFeedback(await listPersonaFeedback(personaId));
      onSelfModelStale();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Konsolidasyon başarısız.");
    } finally {
      setBusy(false);
    }
  }

  const strengths = selfModel.strengths ?? [];
  const weaknesses = selfModel.weaknesses ?? [];
  const reinforce = lessons.filter((l) => l.polarity === "reinforce");
  const correct = lessons.filter((l) => l.polarity === "correct");

  return (
    <div className="space-y-5">
      <SectionTitle
        title="Öğrenilmiş Davranışlar"
        hint="Tartışmalarda verdiğin beğeni ve eleştiriler burada davranış kuralına dönüşür; her kural personanın bir sonraki turunun talimatına eklenir."
        action={
          <Btn size="sm" tone="primary" onClick={handleConsolidate} loading={busy}>
            {pendingFeedback > 0 ? `${pendingFeedback} Geri Bildirimi İşle` : "Yeniden İşle"}
          </Btn>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {result && (
        <div className="rounded-lg border border-violet-500/25 bg-violet-500/[0.07] px-3 py-2 text-[12px] leading-relaxed text-violet-200">
          {result.processed > 0
            ? `${result.processed} geri bildirim işlendi, ${result.lessons} yeni ders çıkarıldı.`
            : result.reason === "insufficient"
              ? `Otomatik işleme için en az ${result.required ?? 3} geri bildirim gerekiyor (şu an ${result.pending ?? 0}).`
              : "İşlenecek yeni geri bildirim yok."}
        </div>
      )}

      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">
              Güçlü Yanları
            </div>
            {strengths.length === 0 ? (
              <p className="text-[11.5px] text-slate-500">Henüz belirlenmedi.</p>
            ) : (
              <ul className="space-y-0.5">
                {strengths.map((s) => (
                  <li key={s} className="text-[12px] text-slate-200">
                    · {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-300/80">
              Zayıf Yanları
            </div>
            {weaknesses.length === 0 ? (
              <p className="text-[11.5px] text-slate-500">Henüz belirlenmedi.</p>
            ) : (
              <ul className="space-y-0.5">
                {weaknesses.map((s) => (
                  <li key={s} className="text-[12px] text-slate-200">
                    · {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {lessons.length === 0 ? (
        <EmptyState
          title="Henüz ders yok"
          description="Bir tartışmada personanın katkısını beğenip beğenmediğini işaretle. Yeterli geri bildirim birikince model bunları somut davranış kurallarına çevirir ve persona onlara uymaya başlar."
        />
      ) : (
        <div className="space-y-3">
          {reinforce.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Sürdürülecek davranışlar
              </h4>
              {reinforce.map((l) => (
                <LessonRow
                  key={l.id}
                  lesson={l}
                  onChanged={() => void refreshLessons()}
                  onError={setError}
                />
              ))}
            </div>
          )}
          {correct.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Düzeltilecek davranışlar
              </h4>
              {correct.map((l) => (
                <LessonRow
                  key={l.id}
                  lesson={l}
                  onChanged={() => void refreshLessons()}
                  onError={setError}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-white/[0.07] pt-4">
        <SectionTitle
          title="Geri Bildirim Geçmişi"
          hint="Tartışma ekranından verilen tüm oylar. 'bekliyor' olanlar bir sonraki işlemede derse dönüşecek."
        />
        <div className="mt-2 space-y-1.5">
          {feedback.length === 0 ? (
            <p className="py-3 text-[12px] text-slate-600">Henüz geri bildirim verilmemiş.</p>
          ) : (
            feedback.map((f) => <FeedbackRow key={f.id} item={f} />)
          )}
        </div>
      </div>
    </div>
  );
}
